// Leitura da ficha botanica. Campo vazio aparece como "—": nao informado nao e "nao".
import type { ReactNode } from "react";
import type { SpeciesProfile, SpeciesProfileCatalogs } from "@/types/species-profile";
import { FIT_TONE, LEVEL_TONE, MONTHS, fmtBool, fmtNumber, fmtRange, label } from "../profileFormat";

interface Props {
  profile: SpeciesProfile;
  catalogs: SpeciesProfileCatalogs;
}

export function Section({ title, icon, children }: { title: string; icon: string; children: ReactNode }) {
  return (
    <section className="bg-white rounded-xl border border-background-200 p-5">
      <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground-900 mb-4">
        <i className={`${icon} text-primary-600`} />
        {title}
      </h2>
      {children}
    </section>
  );
}

function Field({ name, value, wide }: { name: string; value: ReactNode; wide?: boolean }) {
  const empty = value === null || value === undefined || value === "" || value === "—";
  return (
    <div className={wide ? "sm:col-span-2 lg:col-span-3" : undefined}>
      <dt className="text-xs text-foreground-500">{name}</dt>
      <dd className={`text-sm mt-0.5 ${empty ? "text-foreground-400" : "text-foreground-800"} whitespace-pre-line`}>
        {empty ? "—" : value}
      </dd>
    </div>
  );
}

function Grid({ children }: { children: ReactNode }) {
  return <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4">{children}</dl>;
}

function Chips({ codes, labels, tone }: { codes: string[]; labels: Record<string, string>; tone: string }) {
  if (codes.length === 0) return <span className="text-foreground-400">—</span>;
  return (
    <div className="flex flex-wrap gap-1.5">
      {codes.map((c) => (
        <span key={c} className={`badge ${tone}`}>{labels[c] ?? c}</span>
      ))}
    </div>
  );
}

function Level({ code, labels }: { code: string | null; labels: Record<string, string> }) {
  if (!code) return <>—</>;
  return <span className={`font-medium ${LEVEL_TONE[code] ?? ""}`}>{labels[code] ?? code}</span>;
}

function MonthStrip({ flowering, fruiting }: { flowering: number[]; fruiting: number[] }) {
  if (flowering.length === 0 && fruiting.length === 0) {
    return <p className="text-sm text-foreground-400">Floração e frutificação não informadas.</p>;
  }
  const row = (name: string, months: number[], on: string) => (
    <div className="flex items-center gap-2">
      <span className="w-24 shrink-0 text-xs text-foreground-500">{name}</span>
      <div className="grid grid-cols-12 gap-0.5 flex-1">
        {MONTHS.map((m, i) => (
          <div
            key={m}
            title={`${m}: ${months.includes(i + 1) ? name.toLowerCase() : "—"}`}
            className={`h-6 rounded text-[10px] flex items-center justify-center ${
              months.includes(i + 1) ? on : "bg-background-100 text-foreground-400"
            }`}
          >
            {m.charAt(0)}
          </div>
        ))}
      </div>
    </div>
  );
  return (
    <div className="space-y-1.5" aria-label="Calendário de floração e frutificação">
      {row("Floração", flowering, "bg-accent-200 text-accent-900 font-medium")}
      {row("Frutificação", fruiting, "bg-primary-200 text-primary-900 font-medium")}
      <div className="flex items-center gap-2">
        <span className="w-24 shrink-0" />
        <div className="grid grid-cols-12 gap-0.5 flex-1">
          {MONTHS.map((m) => (
            <span key={m} className="text-[10px] text-center text-foreground-400">{m}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

const FIT_ROWS: [keyof SpeciesProfile, string][] = [
  ["fit_narrow_sidewalk", "Calçada estreita"],
  ["fit_wide_sidewalk", "Calçada larga"],
  ["fit_under_power_lines", "Sob fiação"],
  ["fit_median_strip", "Canteiro central"],
  ["fit_parks_squares", "Praças e parques"],
];

export default function ProfileView({ profile: p, catalogs: c }: Props) {
  return (
    <div className="space-y-4">
      <Section title="Identificação" icon="ri-leaf-line">
        <Grid>
          <Field name="Descrição" value={p.description} wide />
          <Field name="Forma de vida" value={label(c.life_forms, p.life_form)} />
          <Field name="Outros nomes populares" value={p.other_popular_names.join(", ")} />
          <Field name="Sinônimos" value={p.synonyms.length ? <i>{p.synonyms.join("; ")}</i> : null} />
          <Field name="Biomas de ocorrência" value={p.native_biomes.map((b) => label(c.biomes, b)).join(", ")} />
          <Field name="Distribuição natural" value={p.natural_distribution} wide />
        </Grid>
      </Section>

      <Section title="Porte e crescimento" icon="ri-ruler-2-line">
        <Grid>
          <Field name="Altura adulta" value={fmtRange(p.adult_height_min_m, p.adult_height_max_m, "m")} />
          <Field name="Diâmetro máximo da copa" value={fmtNumber(p.crown_diameter_max_m, "m")} />
          <Field name="Crescimento" value={label(c.growth_rates, p.growth_rate)} />
          <Field name="Longevidade" value={label(c.longevity, p.longevity)} />
          <Field name="Folhagem" value={label(c.foliage, p.foliage)} />
        </Grid>
      </Section>

      <Section title="Fenologia" icon="ri-calendar-2-line">
        <MonthStrip flowering={p.flowering_months} fruiting={p.fruiting_months} />
        <div className="mt-4">
          <Grid>
            <Field name="Cor da flor" value={p.flower_color} />
            <Field name="Tipo de fruto" value={p.fruit_type} />
            <Field name="Fruto comestível" value={fmtBool(p.edible_fruit)} />
            <Field name="Polinização e dispersão" value={p.pollination_dispersal} wide />
          </Grid>
        </div>
      </Section>

      <Section title="Estrutura" icon="ri-node-tree">
        <Grid>
          <Field name="Sistema radicular" value={label(c.root_systems, p.root_system)} />
          <Field name="Agressividade da raiz" value={<Level code={p.root_aggressiveness} labels={c.levels} />} />
          <Field name="Resistência da madeira" value={label(c.levels, p.wood_strength)} />
          <Field name="Tendência à queda de galhos" value={<Level code={p.branch_drop_tendency} labels={c.levels} />} />
        </Grid>
      </Section>

      <Section title="Adequação ao ambiente urbano" icon="ri-community-line">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2 mb-4">
          {FIT_ROWS.map(([key, name]) => {
            const code = p[key] as string | null;
            return (
              <div key={key} className={`rounded-lg px-3 py-2 ${code ? FIT_TONE[code] : "bg-background-50 text-foreground-400"}`}>
                <div className="text-xs opacity-80">{name}</div>
                <div className="text-sm font-medium">{code ? label(c.fit, code) : "Não avaliada"}</div>
              </div>
            );
          })}
        </div>
        <Grid>
          <Field name="Área permeável mínima" value={fmtNumber(p.min_permeable_area_m2, "m²")} />
          <Field name="Distância mínima de edificação" value={fmtNumber(p.min_distance_building_m, "m")} />
          <Field name="Observações" value={p.urban_fit_notes} wide />
        </Grid>
      </Section>

      <Section title="Benefícios e problemas" icon="ri-scales-3-line">
        <Grid>
          <Field name="Benefícios" value={<Chips codes={p.benefit_codes} labels={c.benefits} tone="bg-primary-50 text-primary-700" />} wide />
          <Field name="Detalhe" value={p.benefits} wide />
          <Field name="Problemas" value={<Chips codes={p.problem_codes} labels={c.problems} tone="bg-danger-50 text-danger-700" />} wide />
          <Field name="Detalhe" value={p.problems} wide />
          <Field name="Toxicidade" value={p.toxicity} wide />
        </Grid>
      </Section>

      <Section title="Manejo" icon="ri-scissors-cut-line">
        <Grid>
          <Field name="Plantio" value={p.planting} wide />
          <Field name="Poda" value={p.pruning} wide />
          <Field name="Época de poda" value={p.pruning_season} />
          <Field name="Tolerância à poda" value={label(c.levels, p.pruning_tolerance)} />
          <Field name="Tolerância ao transplante" value={label(c.levels, p.transplant_tolerance)} />
          <Field name="Irrigação" value={p.irrigation} wide />
          <Field name="Adubação" value={p.fertilization} wide />
          <Field name="Produção de mudas" value={p.seedling_production} wide />
          <Field name="Observações de manejo" value={p.management_notes} wide />
        </Grid>
      </Section>

      <Section title="Conservação e legislação" icon="ri-government-line">
        <Grid>
          <Field name="Grau de ameaça" value={label(c.conservation, p.conservation_status)} />
          <Field name="Corte restrito" value={fmtBool(p.cutting_restricted)} />
          <Field name="Proteção legal" value={p.legal_protection} wide />
        </Grid>
      </Section>

      <Section title="Fontes" icon="ri-book-2-line">
        {p.sources.length === 0 ? (
          <p className="text-sm text-foreground-400">Nenhuma fonte registrada.</p>
        ) : (
          <ul className="space-y-1.5 text-sm">
            {p.sources.map((s, i) =>
              s.aviso ? (
                <li key={i} className="text-warning-800"><i className="ri-information-line mr-1" />{s.aviso}</li>
              ) : (
                <li key={i} className="text-foreground-700">
                  {s.titulo}
                  {s.uso && <span className="text-foreground-500"> — {s.uso}</span>}
                </li>
              )
            )}
          </ul>
        )}
      </Section>
    </div>
  );
}

